import { FC, useContext } from "react";

import { FormCategory, FormType } from "@simplyk/common";

import { FrontendTicketingContext } from "../../../contexts/FrontendTicketingContext";
import { useFormV2Context } from "../context/FormV2Context";
import { HeartsAnimationProvider } from "../context/heartsAnimationContext";

import { AuctionWelcomeBack } from "./AuctionWelcomeBack";
import { AuctionCheckoutFooter } from "./CheckoutFooter/AuctionCheckoutFooter";
import { OccurrenceSelectorProvider } from "./OccurenceSelector/OccurenceSelectorContext";
import { TicketingSelectionV2 } from "./TicketingSelectionV2";
import { TicketingV2AdditionalDonation } from "./TicketingV2AdditionalDonation";

import { NotificationBannerHeightContext } from "@/components/NotificationBanners/NotificationBannerHeightContext";
import { useEnterKeyListener } from "@/features/FormV2/hooks/useEnterKeyListener";
import { useThemedFormZeffyLogoUrl } from "@/features/FormV2/hooks/useThemedFormZeffyLogoUrl";
import { useUtmForm } from "@/features/FormV2/hooks/useUtmForm";
import { AuctionV2Step1Layout } from "@/features/FormV2/Step1/AuctionV2Step1Layout";
import { useAuctionFirstStepSubmit } from "@/features/FormV2/Step1/CheckoutFooter/useAuctionFirstStepSubmit";
import { FormV2GenerateReceiptInfobox } from "@/features/FormV2/Step1/FormV2GenerateReceiptInfobox";
import { FormV2LogoChip } from "@/features/FormV2/Step1/FormV2LogoChip";
import { FormV2ZeffyBusinessModelInfobox } from "@/features/FormV2/Step1/FormV2ZeffyBusinessModelInfobox";
import { MobileFormV2Banner } from "@/features/FormV2/Step1/MobileFormV2Banner";
import { AuctionFormV2Summary } from "@/features/FormV2/Step1/Summary/AuctionFormV2Summary";
import { useTrackThemedFormLogoClicked } from "@/features/FormV2/tracking/useTrackThemedFormLogoClicked";
import { usePreviewContext } from "@/features/LiveFormEditor/LivePreview/context/PreviewContext";

export type AuctionV2Step1Props = {
  isEmbed?: boolean;
};

export const AuctionV2Step1: FC<AuctionV2Step1Props> = ({ isEmbed = false }) => {
  const { formObject, hasBanner, currentStep, description } = useFormV2Context(FormType.Ticketing);
  const { ticketingIsClosed } = useContext(FrontendTicketingContext);
  const { onFirstStepSubmit, canSubmitFirstStep } = useAuctionFirstStepSubmit();
  const { isPreview } = usePreviewContext();
  const { isExpanded, bannerHeight } = useContext(NotificationBannerHeightContext);
  const utm = useUtmForm();

  const onTrackLogoClick = useTrackThemedFormLogoClicked({
    formType: FormType.Ticketing,
    formCategory: FormCategory.Auction,
    country: formObject.organization?.country,
    formStep: 1,
  });

  const logoHref = useThemedFormZeffyLogoUrl(utm);

  const isOneColumnLayout = !hasBanner && !description;

  const showCheckoutFooter = canSubmitFirstStep || isPreview;

  useEnterKeyListener(onFirstStepSubmit, currentStep === "selection");

  return (
    <OccurrenceSelectorProvider>
      <HeartsAnimationProvider>
        <AuctionV2Step1Layout
          contextDrawerHeight={isExpanded ? bannerHeight : 0}
          isEmbed={isEmbed}
          isOneColumn={isOneColumnLayout}
          slots={{
            banner: hasBanner ? <MobileFormV2Banner /> : undefined,
            organizationLogo: (
              <FormV2LogoChip organizationName={formObject.organization!.name} logoUrl={formObject.logoUrl} />
            ),
            summary: <AuctionFormV2Summary />,
            welcomeBack: <AuctionWelcomeBack />,
            additionalDonation: <TicketingV2AdditionalDonation />,
            rates: <TicketingSelectionV2 />,
            generateReceipt: <FormV2GenerateReceiptInfobox />,
            zeffyBusinessModel: <FormV2ZeffyBusinessModelInfobox />,
            checkoutFooter: <AuctionCheckoutFooter />,
          }}
          additionalDonationPosition={ticketingIsClosed ? "top-of-page" : "default"}
          showCheckoutFooter={showCheckoutFooter}
          logoHref={logoHref}
          onLogoClick={onTrackLogoClick}
        />
      </HeartsAnimationProvider>
    </OccurrenceSelectorProvider>
  );
};
